import { untranslated, useTranslation } from "@i18n";
import React, { useEffect, useState, forwardRef, useMemo, RefObject, ReactNode } from "react";
import IconComponent from "../../icons";
import { useResizeDetector } from "react-resize-detector";
import Button from "@mui/material/Button";
import ButtonComponent from "../../form/ButtonComponent";
import { BUTTON_TYPES } from "../../constants/globalVariable";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ToolTipComponent from "../tooltip";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CheckBoxComponent from "@CubeComponents/Mui_Form/CheckBox";
import IconButton from "@mui/material/IconButton";
import Text from "../Text/Text";

export interface ToolbarItem {
  id: string;
  name?: string | ReactNode;
  tooltip?: string;
  cssClass?: string;
  prefixIcon?: string;
  suffixIcon?: string;
  prefixIconTemplate?: ReactNode;
  suffixIconTemplate?: ReactNode;
  template?: () => ReactNode;
  rightAllign?: boolean | string;
  sticky?: boolean;
  buttonType?: string;
  disabled?: boolean;
  hidden?: boolean;
  /** Spread into `React.createElement(item.template, item.props)`, so it is a React props bag. */
  props?: Record<string, unknown>;
}

export interface ColumnDefinition {
  id: string;
  name?: string | HTMLElement;
  hidden?: boolean;
  /** Columns the grid never offers in the chooser (selection checkbox, row-move handle, ...). */
  alwaysHidden?: boolean;
}

export interface ExcelExportExtension {
  exportToExcel: () => void;
}

/**
 * What ToolBar is actually handed as its forwarded ref. It is never attached to a DOM node here -
 * the scrollable strip uses the internal `toolRef` - because SlickGrid/index.tsx forwards the grid
 * instance instead. `gridService` is the only member read off it, purely as a "grid is mounted"
 * signal, so that is all this handle claims.
 */
export interface ToolBarGridHandle {
  gridService?: unknown;
}

interface ToolBarProps {
  toolbar?: ToolbarItem[];
  toolbarClickHandler?: (id: string) => void;
  showToolbarBorder?: boolean;
  enableColumnChooser?: boolean;
  columnDefinitions?: ColumnDefinition[];
  propertiesPanel?: boolean;
  callbackForVisibleColumnsUpdate?: (columns: ColumnDefinition[]) => void;
  height?: number;
  excelExportExtension?: ExcelExportExtension | null;
  excelExport?: boolean;
}

const ToolBar = forwardRef<ToolBarGridHandle, ToolBarProps>(
  (
    {
      toolbar = [],
      toolbarClickHandler,
      showToolbarBorder = false,
      enableColumnChooser = false,
      columnDefinitions = [],
      propertiesPanel,
      callbackForVisibleColumnsUpdate,
      height = 12,
      excelExportExtension,
      excelExport,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const [showOverFlowBtns, setShowOverFlowBtns] = useState(false);
    const { width, ref: toolRef } = useResizeDetector();
    // A `ForwardedRef` is `null` when the caller passed no ref and a callback when it passed one;
    // only the object form can be read back, so the other two shapes have no grid to report.
    const gridRef: RefObject<ToolBarGridHandle | null> | null = typeof ref === "object" ? ref : null;
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [showPropertiesPanel, setShowPropertiesPanel] = useState(false);
    const [selectedColumns, setSelectedColumns] = useState<ColumnDefinition[]>([]);

    useEffect(() => {
      const ele = toolRef.current;
      if (!ele) return;
      const isFullyScrolled = ele.scrollWidth - ele.scrollLeft === ele.clientWidth;
      setShowOverFlowBtns(!isFullyScrolled);
    }, [width, toolRef]);

    const rightArray = useMemo(
      () => toolbar.filter((item: ToolbarItem) => item.rightAllign && !item.sticky),
      [toolbar],
    );
    const leftArray = useMemo(
      () => toolbar.filter((item: ToolbarItem) => !item.rightAllign && !item.sticky),
      [toolbar],
    );
    const rightStickyArray = useMemo(
      () => toolbar.filter((item: ToolbarItem) => item.rightAllign && item.sticky),
      [toolbar],
    );
    const leftStickyArray = useMemo(
      () => toolbar.filter((item: ToolbarItem) => !item.rightAllign && item.sticky),
      [toolbar],
    );

    useEffect(() => {
      if (gridRef?.current?.gridService) {
        const visibleColumns = columnDefinitions.filter((col: ColumnDefinition) => !col.hidden);
        setSelectedColumns(visibleColumns);
      }
    }, [columnDefinitions, gridRef]);

    const handleColumnsChange = (column: ColumnDefinition) => {
      if (!gridRef?.current) return;
      callbackForVisibleColumnsUpdate?.([column]);
    };

    const handleColumnChooserOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(e.currentTarget);
    };

    const handleColumnChooserClose = () => {
      setAnchorEl(null);
    };

    const handleArrowClick = (direction: "forward" | "backward") => {
      if (toolRef.current) {
        const scrollAmount = direction === "forward" ? 200 : -200;
        toolRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
      }
    };

    const handleClick = (id: string) => {
      toolbarClickHandler?.(id);
    };

    const handleExportToExcel = () => {
      excelExportExtension?.exportToExcel();
    };

    const handleToggleProperties = () => {
      setShowPropertiesPanel((prev) => !prev);
      toolbarClickHandler?.("properties");
    };

    const renderToolbarItem = (item: ToolbarItem, index: number) => {
      // `name` is the button's label and is rendered as children below, so it is held back from the
      // blanket spread: the DOM `name` attribute it would otherwise land on only takes a string.
      const { name, ...buttonProps } = item;
      return (
        <div
          className={`flex items-center ${
            item.disabled
              ? "opacity-40 cursor-not-allowed grayscale select-none pointer-events-auto [&>*]:pointer-events-none"
              : ""
          } ${item.hidden ? " hidden" : ""}`}
          key={index}
        >
          {!item.template && (
            <ButtonComponent
              // Every other toolbar key is still forwarded verbatim.
              {...buttonProps}
              name={typeof name === "string" ? name : undefined}
              key={item.id}
              sx={{ flexShrink: 0 }}
              className={`${index === 0 ? "ms-3" : ""} ${" " + (item.cssClass || "")} `}
              buttonType={item.buttonType || BUTTON_TYPES.BACKGROUND_BUTTON}
              onClick={() => {
                handleClick(item.id);
              }}
            >
              <div className="flex items-center">
                <div className="me-1">
                  {item.prefixIcon && (
                    <IconComponent
                      className={`${item.disabled ? "opacity-45" : ""}`}
                      name={item.prefixIcon}
                      width="1.5rem"
                      height="1.5rem"
                    />
                  )}
                  {item.prefixIconTemplate}
                </div>
                {name}
                <div className="ms-1">
                  {item.suffixIcon && (
                    <IconComponent
                      className={`${item.disabled ? "opacity-45" : ""}`}
                      name={item.suffixIcon}
                      width="1.5rem"
                      height="1.5rem"
                    />
                  )}
                  {item.suffixIconTemplate}
                </div>
              </div>
            </ButtonComponent>
          )}
          {item.template &&
            (item.props
              ? React.createElement(item.template as React.FunctionComponent<Record<string, unknown>>, item.props)
              : (item.template as () => ReactNode)())}
        </div>
      );
    };

    return (
      <>
        {toolbar.length > 0 && (
          <div
            className={`overflow-hidden flex gap-5 items-center bg-white ${
              showToolbarBorder && "border-b border-[var(--button-light)]"
            } h-${height}`}
            data-testid="toolbar"
          >
            {showOverFlowBtns && (
              <div className="flex-shrink-0 flex items-center h-full cursor-pointer">
                <Button
                  sx={{
                    height: "100%",
                    minWidth: "2.25rem",
                    borderInlineEnd: "0.0625rem solid #D1D5DB",
                    borderRadius: "0.5rem 0 0 0.5rem",
                    color: "#6B7280",
                    boxShadow: "none",
                    "&:hover": { backgroundColor: "#E5E7EB" },
                  }}
                  onClick={() => handleArrowClick("backward")}
                  size="small"
                >
                  <ArrowBackIosNewIcon sx={{ fontSize: "1.125rem" }} />
                </Button>
              </div>
            )}
            <div
              ref={toolRef}
              className="flex-grow flex items-center justify-between h-full max-w-full gap-1 overflow-hidden"
            >
              <div className="flex gap-5 items-center">
                {leftArray.map((item: ToolbarItem, index: number) =>
                  item.tooltip ? (
                    <ToolTipComponent
                      position="top"
                      key={`start-${item.id}-${index}`}
                      content={untranslated(item.tooltip)}
                    >
                      {renderToolbarItem(item, index)}
                    </ToolTipComponent>
                  ) : (
                    renderToolbarItem(item, index)
                  ),
                )}
              </div>
              <div className="flex items-center">
                {rightArray.map((item: ToolbarItem, index: number) =>
                  item.tooltip ? (
                    <ToolTipComponent
                      position="top"
                      key={`end-scrollable-${item.id}-${index}`}
                      content={untranslated(item.tooltip)}
                    >
                      {renderToolbarItem(item, index)}
                    </ToolTipComponent>
                  ) : (
                    renderToolbarItem(item, index)
                  ),
                )}
              </div>
            </div>
            <div className="flex-shrink-0 flex items-center">
              {leftStickyArray.map((item: ToolbarItem, index: number) =>
                item.tooltip ? (
                  <ToolTipComponent
                    position="top"
                    key={`start-sticky-${item.id}-${index}`}
                    content={untranslated(item.tooltip)}
                  >
                    {renderToolbarItem(item, index)}
                  </ToolTipComponent>
                ) : (
                  renderToolbarItem(item, index)
                ),
              )}
              {enableColumnChooser && (
                <>
                  <ButtonComponent
                    buttonType={BUTTON_TYPES.TEXT_BUTTON}
                    className="ms-2"
                    onClick={handleColumnChooserOpen}
                  >
                    <div className="flex items-center gap-1">
                      <Text>{t("Column Chooser")}</Text> <ArrowDropDownIcon />
                    </div>
                  </ButtonComponent>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleColumnChooserClose}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                    slotProps={{
                      paper: { sx: { overflow: "auto", maxHeight: "31.25rem" } },
                    }}
                  >
                    <div
                      style={{
                        padding: "0.5rem 1rem",
                        borderBottom: "0.0625rem solid rgba(0, 0, 0, 0.12)",
                        position: "sticky",
                        top: 0,
                        backgroundColor: "#fff",
                        zIndex: 1,
                      }}
                    >
                      <TextField
                        fullWidth
                        size="small"
                        variant="standard"
                        placeholder={t("Search columns...")}
                        value={searchTerm}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                        onClick={(e: React.SyntheticEvent) => e.stopPropagation()}
                        onKeyDown={(e: React.SyntheticEvent) => e.stopPropagation()}
                        sx={{ paddingBottom: "0.25rem" }}
                        slotProps={{
                          input: { disableUnderline: true },
                        }}
                      />
                    </div>
                    <div style={{ overflowY: "auto", flexGrow: 1 }}>
                      {(() => {
                        const filteredColumns = columnDefinitions
                          .filter((col: ColumnDefinition) => col.id != "_checkbox_selector" && !col.alwaysHidden)
                          .filter((col: ColumnDefinition) => {
                            const nameString = typeof col.name === "string" ? col.name : col.name?.textContent || "";
                            return nameString.toLowerCase().includes(searchTerm.toLowerCase());
                          });
                        if (filteredColumns.length === 0) {
                          return (
                            <Typography
                              variant="body2"
                              sx={{ p: "0.5rem", color: "text.secondary", textAlign: "center" }}
                            >
                              <Text>{t("No matching columns")}</Text>
                            </Typography>
                          );
                        }
                        return filteredColumns.map((col: ColumnDefinition) => (
                          <MenuItem
                            key={col.id}
                            onClick={(e: { stopPropagation: () => void }) => {
                              e.stopPropagation();
                              handleColumnsChange(col);
                            }}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.25rem",
                              py: "0.25rem",
                              px: "1rem",
                              "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                            }}
                            disableRipple
                          >
                            <CheckBoxComponent
                              checked={selectedColumns.some((c: ColumnDefinition) => c.id === col.id)}
                              size="small"
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                handleColumnsChange(col);
                              }}
                            />
                            <span id={`col-label-${col.id}`}>
                              {typeof col.name === "string" ? col.name : col.name?.textContent || ""}
                            </span>
                          </MenuItem>
                        ));
                      })()}
                    </div>
                  </Menu>
                </>
              )}
              {excelExport && (
                <ButtonComponent className="me-2" buttonType={BUTTON_TYPES.TEXT_BUTTON} onClick={handleExportToExcel}>
                  <div className="flex gap-1 items-center">
                    <IconComponent name="ExcelExportIcon" width="1rem" height="1rem" />
                    <Text>{t("Export")}</Text>
                  </div>
                </ButtonComponent>
              )}
              {propertiesPanel && (
                <IconButton
                  onClick={handleToggleProperties}
                  className={enableColumnChooser ? "ms-2" : ""}
                  data-testid="propertyPanelBtn"
                >
                  <IconComponent
                    name={showPropertiesPanel ? "PropertiesPanelOpen" : "PropertiesPanelClosed"}
                    width="1rem"
                    height="1rem"
                  />
                </IconButton>
              )}
              {rightStickyArray.map((item: ToolbarItem, index: number) =>
                item.tooltip ? (
                  <ToolTipComponent
                    position="top"
                    key={`end-sticky-${item.id}-${index}`}
                    content={untranslated(item.tooltip)}
                  >
                    {renderToolbarItem(item, index)}
                  </ToolTipComponent>
                ) : (
                  renderToolbarItem(item, index)
                ),
              )}
            </div>
            {showOverFlowBtns && (
              <div className="flex-shrink-0 flex items-center h-full cursor-pointer">
                <Button
                  sx={{
                    height: "100%",
                    minWidth: "2.25rem",
                    borderInlineStart: "0.0625rem solid #D1D5DB",
                    borderRadius: "0 0.5rem 0.5rem 0",
                    color: "#6B7280",
                    boxShadow: "none",
                    "&:hover": { backgroundColor: "#E5E7EB" },
                  }}
                  onClick={() => handleArrowClick("forward")}
                  size="small"
                >
                  <ArrowForwardIosIcon sx={{ fontSize: "1.125rem" }} />
                </Button>
              </div>
            )}
          </div>
        )}
      </>
    );
  },
);

ToolBar.displayName = "ToolBar";
export default ToolBar;
